import React, { useContext, useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { AuthContext, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import CredentialsExport from "@/components/CredentialsExport";
import MessageComposer from "@/components/MessageComposer";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { QHubBuddyLogo, QHubFooter } from "@/components/QHubBranding";
import { UserPlus, Users, Link2, Shield, ShieldAlert, GraduationCap, Eye, EyeOff, LogOut, BarChart3, BookOpen, FileUp } from "lucide-react";
import { toast } from "sonner";
import InstitutionMetricsPanel from "@/components/InstitutionMetricsPanel";
import InstitutionAnalyticsPanel from "@/components/InstitutionAnalyticsPanel";
import FloatingAIChat from "@/components/FloatingAIChat";
import CurriculumPreviewSection from "@/components/CurriculumPreviewSection";
import RosterImportWizard from "@/pages/RosterImportWizard";
import AvanzaConsentPanel from "@/components/AvanzaConsentPanel";
import { IncidentResponsiblePanel, IncidentConfigNotice } from "@/components/IncidentResponsiblePanel";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";

const ROLE_LABELS = { parent: "Padre/Madre", teacher: "Docente", student: "Estudiante" };
const ROLE_COLORS = { parent: "bg-blue-50 text-blue-700", teacher: "bg-green-50 text-green-700", student: "bg-purple-50 text-purple-700", institutional_admin: "bg-amber-50 text-amber-700", super_admin: "bg-red-50 text-red-700" };

// M3.3 · Institutional Admin control · vincular padres/tutores a un estudiante.
// Reutiliza el student_profile canónico (uno solo por student_user_id) y
// hace $addToSet sobre parent_user_ids. Zero regresión sobre B2C legacy.
const ParentLinkCell = ({ student, parents }) => {
  const [open, setOpen] = useState(false);
  const [linked, setLinked] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState("");
  const loadLinked = useCallback(async () => {
    try {
      setLoading(true);
      const r = await axios.get(`${API}/admin/parent-student/${student.id}`,
        { withCredentials: true });
      setLinked(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
      setLinked({ parents: [], total: 0 });
    } finally { setLoading(false); }
  }, [student.id]);
  useEffect(() => { if (open) loadLinked(); }, [open, loadLinked]);
  const doLink = async () => {
    if (!selected) return;
    try {
      const r = await axios.post(`${API}/admin/parent-student/link`,
        { student_user_id: student.id, parent_user_id: selected },
        { withCredentials: true });
      toast.success(r.data?.already_linked ? "Ya estaba vinculado" : "Padre vinculado");
      setSelected("");
      await loadLinked();
    } catch (e) {
      const d = e.response?.data?.detail;
      const msg = d === "cross_tenant_denied" ? "Fuera de tu institución"
        : d === "parent_inactive" ? "El padre está inactivo"
        : d === "student_inactive" ? "El estudiante está inactivo"
        : d || "Error al vincular";
      toast.error(msg);
    }
  };
  const doUnlink = async (parent_user_id) => {
    try {
      await axios.post(`${API}/admin/parent-student/unlink`,
        { student_user_id: student.id, parent_user_id },
        { withCredentials: true });
      toast.success("Padre desvinculado");
      await loadLinked();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    }
  };
  const linkedIds = new Set((linked?.parents || []).map(p => p.id));
  const available = parents.filter(p => !linkedIds.has(p.id));
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          data-testid={`link-parent-btn-${student.id}`}
          className="gap-1"
        >
          <Link2 className="w-3.5 h-3.5" />
          Vincular padre
          {linked?.total > 0 && (
            <Badge className="bg-blue-50 text-blue-700 ml-1">{linked.total}</Badge>
          )}
        </Button>
      </DialogTrigger>
      <DialogContent data-testid={`link-parent-dialog-${student.id}`}>
        <DialogHeader>
          <DialogTitle>Padres/Tutores de {student.name || student.username}</DialogTitle>
          <DialogDescription>
            Vincula un padre o tutor existente. Puede tener varios padres
            simultáneamente. El historial académico no se modifica.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <p className="text-xs text-gray-500 mb-1">Vinculados</p>
            {loading && <p className="text-sm text-gray-400">Cargando…</p>}
            {!loading && (linked?.parents || []).length === 0 && (
              <p className="text-sm text-gray-400">Ninguno todavía.</p>
            )}
            <ul className="space-y-1">
              {(linked?.parents || []).map(p => (
                <li
                  key={p.id}
                  className="flex items-center justify-between text-sm border rounded px-2 py-1"
                  data-testid={`linked-parent-${p.id}`}
                >
                  <span>{p.name || p.email}</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-red-600"
                    onClick={() => doUnlink(p.id)}
                    data-testid={`unlink-parent-${p.id}`}
                  >Desvincular</Button>
                </li>
              ))}
            </ul>
          </div>
          <div className="pt-3 border-t">
            <p className="text-xs text-gray-500 mb-1">Añadir padre existente</p>
            <div className="flex gap-2">
              <Select value={selected} onValueChange={setSelected}>
                <SelectTrigger data-testid={`link-parent-select-${student.id}`}>
                  <SelectValue placeholder="Selecciona un padre..." />
                </SelectTrigger>
                <SelectContent>
                  {available.length === 0 && (
                    <SelectItem value="__none__" disabled>
                      Sin padres disponibles
                    </SelectItem>
                  )}
                  {available.map(p => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.name} — {p.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                onClick={doLink}
                disabled={!selected || selected === "__none__"}
                data-testid={`link-parent-confirm-${student.id}`}
              >Vincular</Button>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cerrar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const InstitutionAdminDashboard = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (e) {
      console.warn("Logout request failed (continuing anyway):", e);
    }
    navigate("/login");
  };

  // Create user state
  const [newName, setNewName] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [newRole, setNewRole] = useState("student");
  const [newGrade, setNewGrade] = useState("");
  const [newParentEmail, setNewParentEmail] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const parentUsers = useMemo(() => allUsers.filter((u) => u.role === "parent"), [allUsers]);

  // M3.4 · Welcome Email state (Preview only, tests only)
  const [welcomeSelected, setWelcomeSelected] = useState(new Set());
  const [welcomeBusy, setWelcomeBusy] = useState(false);
  const [welcomeConfirmOpen, setWelcomeConfirmOpen] = useState(false);
  const [welcomeSummary, setWelcomeSummary] = useState(null);

  const isPendingActivation = (u) => u?.must_change_password === true;

  const toggleWelcomeOne = (uid) => {
    setWelcomeSelected(prev => {
      const s = new Set(prev);
      if (s.has(uid)) s.delete(uid); else s.add(uid);
      return s;
    });
  };
  const toggleWelcomeAllPending = () => {
    const pendingIds = allUsers.filter(isPendingActivation).map(u => u.id);
    setWelcomeSelected(prev => {
      const s = new Set(prev);
      const allSelected = pendingIds.every(id => s.has(id));
      if (allSelected) pendingIds.forEach(id => s.delete(id));
      else pendingIds.forEach(id => s.add(id));
      return s;
    });
  };

  const sendWelcomeIndividual = async (uid) => {
    try {
      setWelcomeBusy(true);
      const r = await axios.post(`${API}/admin/welcome-email/send`,
        { user_id: uid }, { withCredentials: true });
      const result = r.data?.result;
      if (result === "sent") toast.success("Bienvenida enviada");
      else if (result === "account_already_activated") toast.info("Cuenta ya activada");
      else if (result === "missing_email") toast.warning("Sin email registrado");
      else if (result === "email_failed") toast.error("Fallo el envío");
      else if (result === "cross_tenant_denied") toast.error("No autorizado");
      else toast.info(result || "OK");
      loadUsers();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al enviar bienvenida");
    } finally { setWelcomeBusy(false); }
  };

  const sendWelcomeBulk = async () => {
    const ids = Array.from(welcomeSelected);
    if (!ids.length) return;
    try {
      setWelcomeBusy(true);
      const r = await axios.post(`${API}/admin/welcome-email/bulk`,
        { user_ids: ids }, { withCredentials: true });
      setWelcomeSummary(r.data?.summary || null);
      setWelcomeSelected(new Set());
      const sent = r.data?.summary?.sent || 0;
      if (sent > 0) toast.success(`${sent} bienvenidas enviadas`);
      const failed = r.data?.summary?.email_failed || 0;
      if (failed > 0) toast.error(`${failed} envíos fallaron`);
      loadUsers();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al enviar bienvenidas");
    } finally {
      setWelcomeBusy(false);
      setWelcomeConfirmOpen(false);
    }
  };

  // Invite teacher state
  const [inviteName, setInviteName] = useState("");
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteLoading, setInviteLoading] = useState(false);

  // Bulk Excel import state
  const [bulkFile, setBulkFile] = useState(null);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);

  // Import state
  const [importLoading, setImportLoading] = useState(false);
  const [importResult, setImportResult] = useState(null);

  // Assignment state
  const [teachers, setTeachers] = useState([]);
  const [studentProfiles, setStudentProfiles] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [assignTeacherEmail, setAssignTeacherEmail] = useState("");
  const [assignStudentProfileId, setAssignStudentProfileId] = useState("");
  const [assignLoading, setAssignLoading] = useState(false);

  // Consents state
  const [consentsLoading, setConsentsLoading] = useState(false);
  const [consents, setConsents] = useState([]);

  useEffect(() => {
    if (!user) return;
    if (user.role !== "institutional_admin") navigate("/dashboard");
  }, [user, navigate]);

  const loadUsers = async () => {
    try {
      setUsersLoading(true);
      const res = await axios.get(`${API}/admin/users`, { withCredentials: true });
      setAllUsers(res.data || []);
    } catch (e) {
      // ignore
    } finally {
      setUsersLoading(false);
    }
  };

  const createUser = async () => {
    try {
      setCreateLoading(true);
      // If the identifier does not contain '@', treat it as a generic
      // username (sin email) — same logic as login. Otherwise treat as email.
      const ident = (newEmail || "").trim();
      const isUsername = ident.length > 0 && !ident.includes("@");
      const body = {
        name: newName,
        password: newPassword,
        role: newRole,
        ...(isUsername ? { username: ident } : { email: ident.toLowerCase() }),
      };
      if (newRole === "student") {
        body.grade = newGrade;
        body.parent_email = newParentEmail;
      }
      await axios.post(`${API}/admin/create-user`, body, { withCredentials: true });
      toast.success(`${ROLE_LABELS[newRole]} creado exitosamente`);
      setNewName(""); setNewEmail(""); setNewPassword(""); setNewGrade(""); setNewParentEmail("");
      loadUsers();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al crear usuario");
    } finally {
      setCreateLoading(false);
    }
  };

  const sendInvite = async () => {
    try {
      setInviteLoading(true);
      await axios.post(`${API}/institution/invite-teacher`, { name: inviteName, email: inviteEmail }, { withCredentials: true });
      toast.success("Invitacion enviada");
      setInviteName(""); setInviteEmail("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al enviar invitacion");
    } finally {
      setInviteLoading(false);
    }
  };

  const runImport = async (file) => {
    try {
      setImportLoading(true); setImportResult(null);
      const form = new FormData();
      form.append("file", file);
      const res = await axios.post(`${API}/institution/import`, form, { withCredentials: true, headers: { "Content-Type": "multipart/form-data" } });
      setImportResult(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al importar CSV");
    } finally {
      setImportLoading(false);
    }
  };

  const downloadBulkTemplate = () => {
    window.open(`${API}/admin/bulk-import/template`, "_blank");
  };

  const runBulkImport = async () => {
    if (!bulkFile) return;
    try {
      setBulkLoading(true);
      setBulkResult(null);
      const form = new FormData();
      form.append("file", bulkFile);
      const res = await axios.post(`${API}/admin/bulk-import/excel`, form, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      setBulkResult(res.data);
      if (res.data.exitosos > 0) {
        toast.success(`${res.data.exitosos} usuarios importados correctamente`);
        loadUsers();
      }
      if (res.data.fallidos > 0) {
        toast.error(`${res.data.fallidos} filas con errores — revisa la tabla`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al importar el archivo");
    } finally {
      setBulkLoading(false);
    }
  };

  const loadAssignmentsData = useCallback(async () => {
    try {
      const [tRes, spRes, aRes] = await Promise.all([
        axios.get(`${API}/institution/teachers`, { withCredentials: true }),
        axios.get(`${API}/institution/student-profiles`, { withCredentials: true }),
        axios.get(`${API}/institution/assignments`, { withCredentials: true }),
      ]);
      setTeachers(tRes.data || []); setStudentProfiles(spRes.data || []); setAssignments(aRes.data || []);
      if (!assignTeacherEmail && tRes.data?.[0]?.email) setAssignTeacherEmail(tRes.data[0].email);
      if (!assignStudentProfileId && spRes.data?.[0]?.id) setAssignStudentProfileId(spRes.data[0].id);
    } catch (e) { /* ignore */ }
  }, [assignTeacherEmail, assignStudentProfileId]);

  const createAssignment = async () => {
    try {
      setAssignLoading(true);
      await axios.post(`${API}/institution/assignments`, { teacher_email: assignTeacherEmail, student_profile_id: assignStudentProfileId }, { withCredentials: true });
      toast.success("Asignacion creada");
      await loadAssignmentsData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al crear asignacion");
    } finally {
      setAssignLoading(false);
    }
  };

  const loadConsents = async () => {
    try { setConsentsLoading(true); const res = await axios.get(`${API}/institution/consents`, { withCredentials: true }); setConsents(res.data || []); } finally { setConsentsLoading(false); }
  };

  useEffect(() => {
    if (user?.role === "institutional_admin") {
      loadUsers();
      loadConsents();
      loadAssignmentsData();
    }
  }, [user?.role]);

  return (
    <div className="min-h-screen flex flex-col bg-mesh" data-testid="institution-admin-dashboard">
      <header className="bg-white/80 backdrop-blur border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <QHubBuddyLogo size="default" />
            <Badge className="bg-blue-50 text-blue-700">Admin Institucional</Badge>
          </div>
          <Button
            variant="ghost"
            onClick={handleLogout}
            className="flex items-center gap-2 text-gray-500 hover:text-gray-900"
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4" /> Cerrar sesión
          </Button>
        </div>
      </header>

      <main className="flex-1 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10 w-full">
        <div className="bg-white rounded-2xl border border-gray-100 p-6">
          <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
            <div>
              <h1 className="text-2xl font-bold font-['Outfit'] text-gray-900 mb-2">Dashboard Institucional</h1>
              <p className="text-sm text-gray-500">Crea usuarios, invita docentes, asigna estudiantes y monitorea consentimientos.</p>
            </div>
            <div className="flex flex-wrap gap-2 items-center">
              <MessageComposer triggerLabel="Contactar al Super Admin" />
              <CredentialsExport />
            </div>
          </div>
          <IncidentConfigNotice />
          <Tabs defaultValue="metrics">
            <TabsList className="mb-6 flex-wrap h-auto justify-start">
              <TabsTrigger value="metrics" data-testid="tab-metrics"><BarChart3 className="w-4 h-4 mr-1.5" />Analítica</TabsTrigger>
              <TabsTrigger value="metrics-basic" data-testid="tab-metrics-basic"><BarChart3 className="w-4 h-4 mr-1.5" />Docentes</TabsTrigger>
              <TabsTrigger value="create-user" data-testid="tab-create-user"><UserPlus className="w-4 h-4 mr-1.5" />Crear Usuario</TabsTrigger>
              <TabsTrigger value="users" data-testid="tab-users"><Users className="w-4 h-4 mr-1.5" />Usuarios</TabsTrigger>
              <TabsTrigger value="invite" data-testid="tab-invite"><Link2 className="w-4 h-4 mr-1.5" />Invitar Docente</TabsTrigger>
              <TabsTrigger value="assignments" data-testid="tab-assignments"><GraduationCap className="w-4 h-4 mr-1.5" />Asignaciones</TabsTrigger>
              <TabsTrigger value="roster-import" data-testid="tab-roster-import"><FileUp className="w-4 h-4 mr-1.5" />Importar Roster</TabsTrigger>
              <TabsTrigger value="consents" data-testid="tab-consents"><Shield className="w-4 h-4 mr-1.5" />Consentimientos</TabsTrigger>
              <TabsTrigger value="curriculum" data-testid="tab-curriculum"><BookOpen className="w-4 h-4 mr-1.5" />Currículo</TabsTrigger>
              <TabsTrigger value="sensitive-incidents" data-testid="tab-sensitive-incidents"><ShieldAlert className="w-4 h-4 mr-1.5" />Incidencias sensibles</TabsTrigger>
            </TabsList>

            <TabsContent value="sensitive-incidents">
              <IncidentResponsiblePanel />
            </TabsContent>

            {/* ===== ANALYTICS (Fase 1) ===== */}
            <TabsContent value="metrics">
              <InstitutionAnalyticsPanel />
            </TabsContent>

            {/* ===== DOCENTES BÁSICO (vista previa) ===== */}
            <TabsContent value="metrics-basic">
              <InstitutionMetricsPanel />
            </TabsContent>

            {/* ===== CREATE USER ===== */}
            <TabsContent value="create-user">
              <div className="max-w-lg space-y-4">
                <div className="p-4 rounded-xl bg-blue-50/50 border border-blue-100">
                  <p className="text-sm text-blue-700 font-medium mb-1">Crear nuevo usuario</p>
                  <p className="text-xs text-blue-600">El usuario podra iniciar sesion con el correo y contrasena que asignes.</p>
                </div>

                <div className="space-y-1">
                  <label className="text-xs text-gray-500 font-medium">Rol</label>
                  <Select value={newRole} onValueChange={setNewRole}>
                    <SelectTrigger data-testid="create-role-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="student">Estudiante</SelectItem>
                      <SelectItem value="parent">Padre/Madre</SelectItem>
                      <SelectItem value="teacher">Docente</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Nombre completo" data-testid="create-name" />
                <Input value={newEmail} onChange={(e) => setNewEmail(e.target.value)} placeholder="Correo electronico" type="email" data-testid="create-email" />

                <div className="relative">
                  <Input value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="Contrasena" type={showPassword ? "text" : "password"} data-testid="create-password" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>

                {newRole === "student" && (
                  <div className="space-y-1">
                    <label className="text-xs text-gray-500 font-medium">Grado</label>
                    <Select value={newGrade} onValueChange={setNewGrade}>
                      <SelectTrigger data-testid="create-grade-select"><SelectValue placeholder="Seleccionar grado" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="4">4 Grado</SelectItem>
                        <SelectItem value="5">5 Grado</SelectItem>
                        <SelectItem value="6">6 Grado</SelectItem>
                        <SelectItem value="7">7 Grado</SelectItem>
                        <SelectItem value="8">8 Grado</SelectItem>
                        <SelectItem value="9">9 Grado</SelectItem>
                        <SelectItem value="1-bach">1 Bachillerato</SelectItem>
                        <SelectItem value="2-bach">2 Bachillerato</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {newRole === "student" && (
                  <div className="space-y-1">
                    <label className="text-xs text-gray-500 font-medium">Padre/Madre vinculado</label>
                    <select
                      className="w-full h-10 rounded-xl border border-gray-200 bg-white px-3 text-sm"
                      value={newParentEmail}
                      onChange={(e) => setNewParentEmail(e.target.value)}
                      data-testid="create-parent-select"
                    >
                      <option value="">Seleccionar padre/madre</option>
                      {parentUsers.map((p) => (
                        <option key={p.id} value={p.email}>{p.name} — {p.email}</option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-400">Debes crear el padre/madre primero.</p>
                  </div>
                )}

                <Button
                  onClick={createUser}
                  disabled={createLoading || !newName || !newEmail || !newPassword || (newRole === "student" && (!newGrade || !newParentEmail))}
                  className="w-full rounded-xl bg-gradient-to-r from-[#419cca] to-[#8b5cf6] text-white"
                  data-testid="create-user-btn"
                >
                  {createLoading ? "Creando..." : `Crear ${ROLE_LABELS[newRole]}`}
                </Button>
              </div>
            </TabsContent>

            {/* ===== USERS LIST ===== */}
            <TabsContent value="users">
              <div className="space-y-4">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <p className="text-sm text-gray-500">Usuarios de la institucion</p>
                  <div className="flex items-center gap-2">
                    {welcomeSelected.size > 0 && (
                      <Button
                        size="sm"
                        onClick={() => setWelcomeConfirmOpen(true)}
                        disabled={welcomeBusy}
                        className="rounded-lg bg-gradient-to-r from-[#419cca] to-[#8b5cf6] text-white"
                        data-testid="welcome-bulk-btn">
                        Enviar bienvenida ({welcomeSelected.size})
                      </Button>
                    )}
                    <Button variant="outline" size="sm" onClick={loadUsers} disabled={usersLoading}>{usersLoading ? "Cargando..." : "Actualizar"}</Button>
                  </div>
                </div>

                {welcomeSummary && (
                  <div className="rounded-xl border border-gray-100 bg-gray-50 p-3 text-xs text-gray-700" data-testid="welcome-summary">
                    Resumen: enviadas <b>{welcomeSummary.sent || 0}</b> ·
                    ya activadas <b>{welcomeSummary.account_already_activated || 0}</b> ·
                    sin email <b>{welcomeSummary.missing_email || 0}</b> ·
                    fallidas <b>{welcomeSummary.email_failed || 0}</b>
                  </div>
                )}

                <div className="rounded-xl border border-gray-100 overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-8">
                          <input
                            type="checkbox"
                            aria-label="Seleccionar todos los pendientes"
                            data-testid="welcome-select-all"
                            checked={
                              allUsers.filter(isPendingActivation).length > 0 &&
                              allUsers.filter(isPendingActivation).every(u => welcomeSelected.has(u.id))
                            }
                            onChange={toggleWelcomeAllPending}
                          />
                        </TableHead>
                        <TableHead>Nombre</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Rol</TableHead>
                        <TableHead>Grado</TableHead>
                        <TableHead>Estado</TableHead>
                        <TableHead className="text-right">Acciones</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {allUsers.map((u) => {
                        const pending = isPendingActivation(u);
                        const hasEmail = !!(u.email && String(u.email).includes("@"));
                        return (
                          <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                            <TableCell>
                              <input
                                type="checkbox"
                                aria-label={`Seleccionar ${u.name}`}
                                data-testid={`welcome-select-${u.id}`}
                                disabled={!pending || !hasEmail}
                                checked={welcomeSelected.has(u.id)}
                                onChange={() => toggleWelcomeOne(u.id)}
                              />
                            </TableCell>
                            <TableCell className="font-medium">{u.name}</TableCell>
                            <TableCell className="text-sm text-gray-600">{u.email || <span className="text-gray-400">sin email</span>}</TableCell>
                            <TableCell><Badge className={ROLE_COLORS[u.role] || "bg-gray-100 text-gray-700"}>{ROLE_LABELS[u.role] || u.role}</Badge></TableCell>
                            <TableCell>{u.grade || "-"}</TableCell>
                            <TableCell>
                              {pending
                                ? <Badge className="bg-amber-50 text-amber-700" data-testid={`user-status-${u.id}`}>Pendiente</Badge>
                                : <Badge className={u.is_active ? "bg-green-50 text-green-700" : "bg-gray-100 text-gray-600"} data-testid={`user-status-${u.id}`}>{u.is_active ? "Activo" : "Inactivo"}</Badge>}
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="inline-flex items-center gap-2 justify-end">
                                {pending && hasEmail && (
                                  <Button
                                    size="sm" variant="outline"
                                    disabled={welcomeBusy}
                                    onClick={() => sendWelcomeIndividual(u.id)}
                                    data-testid={`welcome-send-${u.id}`}
                                  >Enviar bienvenida</Button>
                                )}
                                {u.role === "student" && (
                                  <ParentLinkCell
                                    student={u}
                                    parents={allUsers.filter(x => x.role === "parent" && x.is_active !== false)}
                                  />
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                      {allUsers.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-gray-400 py-8">No hay usuarios registrados</TableCell></TableRow>}
                    </TableBody>
                  </Table>
                </div>

                <Dialog open={welcomeConfirmOpen} onOpenChange={setWelcomeConfirmOpen}>
                  <DialogContent data-testid="welcome-confirm-dialog">
                    <DialogHeader>
                      <DialogTitle>Enviar bienvenida a {welcomeSelected.size} usuario(s)</DialogTitle>
                      <DialogDescription>
                        Los usuarios recibirán un correo con un enlace para crear su contraseña.
                        Solo se procesarán usuarios con estado <b>Pendiente</b> y email registrado.
                        El enlace expira en 24 horas y es de un solo uso.
                      </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setWelcomeConfirmOpen(false)} disabled={welcomeBusy}>Cancelar</Button>
                      <Button onClick={sendWelcomeBulk} disabled={welcomeBusy} data-testid="welcome-confirm-btn"
                        className="rounded-lg bg-gradient-to-r from-[#419cca] to-[#8b5cf6] text-white">
                        {welcomeBusy ? "Enviando..." : "Confirmar envío"}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>
            </TabsContent>

            {/* ===== INVITE TEACHER ===== */}
            <TabsContent value="invite">
              <div className="max-w-lg space-y-3">
                <div className="p-4 rounded-xl bg-gray-50 border border-gray-100">
                  <p className="text-sm text-gray-700 font-medium mb-1">Invitar docente por Magic Link</p>
                  <p className="text-xs text-gray-500">El docente recibira un enlace por email para activar su cuenta.</p>
                </div>
                <Input value={inviteName} onChange={(e) => setInviteName(e.target.value)} placeholder="Nombre" data-testid="invite-name" />
                <Input value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} placeholder="Email" type="email" data-testid="invite-email" />
                <Button onClick={sendInvite} disabled={inviteLoading || !inviteName || !inviteEmail} className="rounded-xl bg-gradient-to-r from-[#419cca] to-[#8b5cf6] text-white" data-testid="invite-btn">
                  {inviteLoading ? "Enviando..." : "Enviar invitacion"}
                </Button>
              </div>
            </TabsContent>

            {/* ===== ASSIGNMENTS ===== */}
            <TabsContent value="assignments">
              <div className="space-y-4">
                <div className="p-4 rounded-xl bg-gray-50 border border-gray-100">
                  <p className="text-sm font-medium text-gray-800 mb-1">Asignar docente a estudiante</p>
                  <p className="text-xs text-gray-500">El docente vera solo los estudiantes asignados.</p>
                </div>
                <div className="grid md:grid-cols-2 gap-4 max-w-3xl">
                  <div className="space-y-2">
                    <p className="text-xs text-gray-500">Docente</p>
                    <select className="w-full h-11 rounded-xl border border-gray-200 bg-white px-3 text-sm" value={assignTeacherEmail} onChange={(e) => setAssignTeacherEmail(e.target.value)} data-testid="assign-teacher-select">
                      {teachers.map((t) => <option key={t.id} value={t.email}>{t.name} - {t.email}</option>)}
                    </select>
                  </div>
                  <div className="space-y-2">
                    <p className="text-xs text-gray-500">Estudiante</p>
                    <select className="w-full h-11 rounded-xl border border-gray-200 bg-white px-3 text-sm" value={assignStudentProfileId} onChange={(e) => setAssignStudentProfileId(e.target.value)} data-testid="assign-student-select">
                      {studentProfiles.map((sp) => <option key={sp.id} value={sp.id}>{sp.student_name} - {sp.grade}</option>)}
                    </select>
                  </div>
                </div>
                <Button onClick={createAssignment} disabled={assignLoading || !assignTeacherEmail || !assignStudentProfileId} className="rounded-xl bg-gradient-to-r from-[#419cca] to-[#8b5cf6] text-white" data-testid="assign-btn">
                  {assignLoading ? "Asignando..." : "Crear asignacion"}
                </Button>
                <div className="rounded-xl border border-gray-100 overflow-hidden">
                  <Table>
                    <TableHeader><TableRow><TableHead>Docente</TableHead><TableHead>Estudiante</TableHead><TableHead>Grado</TableHead><TableHead>Estado</TableHead></TableRow></TableHeader>
                    <TableBody>
                      {assignments.map((a) => (
                        <TableRow key={a.id}>
                          <TableCell className="text-sm">{a.teacher?.name || a.teacher_email}<div className="text-xs text-gray-400">{a.teacher?.email || a.teacher_email}</div></TableCell>
                          <TableCell className="font-medium">{a.student_profile?.student_name}</TableCell>
                          <TableCell>{a.student_profile?.grade}</TableCell>
                          <TableCell><Badge className={a.teacher_user_id ? "bg-green-50 text-green-700" : "bg-yellow-50 text-yellow-800"}>{a.teacher_user_id ? "activa" : "pendiente"}</Badge></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </TabsContent>

            {/* ===== CONSENTS ===== */}
            <TabsContent value="consents">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-gray-500">Estado de consentimiento por padre/madre</p>
                  <Button variant="outline" onClick={loadConsents} disabled={consentsLoading}>{consentsLoading ? "Actualizando..." : "Actualizar"}</Button>
                </div>
                <div className="rounded-xl border border-gray-100 overflow-hidden">
                  <Table>
                    <TableHeader><TableRow><TableHead>Padre/Madre</TableHead><TableHead>Email</TableHead><TableHead>Estudiantes</TableHead><TableHead>Consentimiento</TableHead><TableHead>Activo</TableHead></TableRow></TableHeader>
                    <TableBody>
                      {consents.map((c) => (
                        <TableRow key={c.parent_user_id}>
                          <TableCell className="font-medium">{c.name}</TableCell>
                          <TableCell>{c.email}</TableCell>
                          <TableCell>{c.students}</TableCell>
                          <TableCell><Badge className={c.consent === "accepted" ? "bg-green-50 text-green-700" : "bg-yellow-50 text-yellow-800"}>{c.consent}</Badge></TableCell>
                          <TableCell><Badge className={c.is_active ? "bg-green-50 text-green-700" : "bg-gray-100 text-gray-700"}>{c.is_active ? "Si" : "No"}</Badge></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                {/* ===== M3.1 HOTFIX · Buddy Avanza paper consent recorder ===== */}
                <div className="pt-6 border-t border-gray-100">
                  <AvanzaConsentPanel />
                </div>
              </div>
            </TabsContent>

            <TabsContent value="curriculum">
              <CurriculumPreviewSection />
            </TabsContent>

            {/* ===== M3.1 · ROSTER IMPORT ===== */}
            <TabsContent value="roster-import">
              <RosterImportWizard />
            </TabsContent>
          </Tabs>
        </div>
      </main>

      <QHubFooter />

      {/* AI Support Bot flotante (solo Institutional Admin) */}
      <FloatingAIChat />
    </div>
  );
};

export default InstitutionAdminDashboard;
